import { z } from "zod";
import type { components } from "#/schema";
import { Beweegthema, GroepeerType } from "#/schema.nswag";
import { apiEnum } from "#/modules/util.helpers/zod-enum";
import { formatLeerlijnOnderdeel } from "#/modules/les.start-flow/onderdelen";
import { minutesToLabel, toTimeSlot } from "#/modules/les.planning/domain/calendar";

const lockedLeerlingSchema = z.object({
  leerlingId: z.string(),
  groep: z.number(),
}) satisfies z.ZodType<components["schemas"]["LesgroepLeerlingLockModel"]>;

/** Fallback aantal groepen when the URL nor the docent's voorkeur provides one. */
export const FALLBACK_AANTAL_GROEPEN = 4;

/** Typed URL search of the les preparation dialog, which every step of it shares. */
export const startLessonFlowSearchSchema = z.object({
  // Filter data used to create the groups
  klasIds: z.array(z.string()).catch([]).default([]),
  // Deliberately no schema default: "absent" means "use the docent's voorkeur". Step 3's
  // beforeLoad backfills it into the URL (like `seed`) so shared step-3 links reproduce the
  // same groups for users with a different voorkeur.
  aantalGroepen: z.number().int().optional().catch(undefined),
  beweegthema: apiEnum(Beweegthema).nullable().catch(null).default(null),
  groepeerType: apiEnum(GroepeerType).catch("Heterogeen").default("Heterogeen"),
  seed: z.string().nullable().catch(null).default(null),
  lockedLeerlingen: z.array(lockedLeerlingSchema).catch([]).default([]),
  // Leerlingen left out of the verdeling: "doet niet mee" at a shuffle, or absent at a redistribution.
  excludedLeerlingen: z.array(z.string()).catch([]).default([]),
  beweegactiviteiten: z.array(z.string()).catch([]).default([]),
  sociaalEmotioneelThemas: z.array(z.string()).catch([]).default([]),
  /** Leerlijn-only lesonderdelen as `Leerlijn` or `Leerlijn:Beweegthema` (see onderdelen.ts). */
  leerlijnen: z.array(z.string()).catch([]).default([]),

  /** The les the preparation dialog holds. */
  editLesId: z.string().nullable().catch(null).default(null),
  /** Rooster slot the planning dialog was opened from; enables "Vervalt deze week". */
  roosterregelId: z.string().nullable().catch(null).default(null),
  /** The lesuur of the docent's lesrooster the les is planned or started in. */
  lesuurId: z.string().nullable().catch(null).default(null),
  /**
   * The colleague whose les it becomes, from their roosteruur; null makes it the docent's own.
   * Whoever starts it gives it, the les stays the colleague's.
   */
  schooldocentId: z.string().nullable().catch(null).default(null),
  /** Which les of its week this is, for what the jaarplanning recommends; null recommends every les. */
  lesnummer: z.number().int().min(1).nullable().catch(null).default(null),

  // Filter data for functionality, not used to create the groups
  beweegactiviteitenreeksSearchTerm: z.string().nullable().catch(null).default(null),
  sociaalEmotioneelThemasReeksSearchTerm: z.string().nullable().catch(null).default(null),
  doelenTab: z.string().catch("beweegactiviteitenreeksen").default("beweegactiviteitenreeksen"),
  /** Calendar date (YYYY-MM-DD); null means today. */
  scheduledDate: z.string().nullable().catch(null).default(null),
  /** Start time (HH:mm); null means now. */
  scheduledTime: z.string().nullable().catch(null).default(null),
  duurMinuten: z.number().int().optional().catch(undefined),
  /** When false: only school's own goals (excludes Beweegbaas defaults). */
  includeDefaultsBeweeg: z.boolean().catch(true).default(true),
  includeDefaultsSociaal: z.boolean().catch(true).default(true),
});

export type StartLessonFilters = z.infer<typeof startLessonFlowSearchSchema>;

/**
 * Canonical default values, derived from the schema itself so they can never drift from it.
 * Paired with the route's `stripSearchParams` middleware to keep default-valued params out of
 * the URL (nuqs parity with the RR7 portal).
 */
export const startLessonFlowSearchDefaults: StartLessonFilters = startLessonFlowSearchSchema.parse(
  {},
);

/**
 * The beweegthema the groepjes are made on: only one the chosen activiteiten have, as the backend
 * saves it. Once its activiteiten left the les, the groepjes are made on the schoolcarrière again.
 */
export function getGroepeerBeweegthema(
  beweegthema: StartLessonFilters["beweegthema"],
  beweegthemas: readonly components["schemas"]["Beweegthema"][],
) {
  return beweegthema !== null && beweegthemas.includes(beweegthema) ? beweegthema : null;
}

/** The search of the preparation dialog for a les as it is saved. */
export function getStartLessonFiltersFromLesEditData(
  lesEditData: components["schemas"]["GetLesEditDataResponse"],
  lesId: string,
): StartLessonFilters {
  const timeSlot = toTimeSlot(lesEditData.date, lesEditData.duurMinuten);
  return {
    ...startLessonFlowSearchDefaults,
    editLesId: lesId,
    scheduledDate: timeSlot.day.toString(),
    scheduledTime: minutesToLabel(timeSlot.startMinutes),
    duurMinuten: lesEditData.duurMinuten,
    leerlijnen: lesEditData.onderdelen.map((onderdeel) =>
      formatLeerlijnOnderdeel({
        leerlijn: onderdeel.leerlijn,
        beweegthema: onderdeel.beweegthema ?? null,
      }),
    ),
    klasIds: lesEditData.klasIds,
    aantalGroepen: lesEditData.filter.aantalGroepen,
    beweegthema: lesEditData.filter.beweegthema ?? null,
    groepeerType: lesEditData.filter.groepeerType,
    beweegactiviteiten: lesEditData.beweegactiviteiten,
    sociaalEmotioneelThemas: lesEditData.sociaalEmotioneelThemas,
    seed: lesEditData.filter.seed,
    lockedLeerlingen: lesEditData.filter.lockedLeerlingen || [],
    excludedLeerlingen: lesEditData.filter.excludedLeerlingen || [],
  };
}
